import React from "react";
import {
  type Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";

import DataTablePagination from "./DataTablePagination";

export interface BaseRowItem {
  id: string | number;
}

function hideBelowClass(hideBelow?: "sm" | "md" | "lg") {
  if (!hideBelow) return "";
  if (hideBelow === "sm") return "hidden sm:table-cell";
  if (hideBelow === "md") return "hidden md:table-cell";
  return "hidden lg:table-cell";
}

function getPaginationSummary(
  currentPage: number,
  limit: number,
  count: number
): string {
  if (count === 0) return "Showing 0 to 0 of 0 entries";
  const startEntry = (currentPage - 1) * limit + 1;
  const endEntry = Math.min(currentPage * limit, count);
  return `Showing ${startEntry} to ${endEntry} of ${count} entries`;
}

function labelForColumn<T extends BaseRowItem>(
  column: Column<T, unknown>
): string {
  const def = column.columnDef;
  if (typeof def.header === "string") return def.header;
  if ("accessorKey" in def && def.accessorKey != null) {
    return String(def.accessorKey);
  }
  return column.id;
}

type ColumnMetaShape = {
  align?: "left" | "right" | "center";
  wrap?: boolean;
};

/**
 * `wrap` on `<td>` uses `max-w-0` + `min-w-0` so long text can shrink inside the
 * table layout. Never apply `max-w-0` to `<th>` — it collapses the header and
 * stacks one character per line.
 */
function metaAlignmentClasses(
  meta?: ColumnMetaShape,
  placement: "header" | "cell" = "cell"
) {
  const allowWrap = meta?.wrap === true;
  return clsx(
    !allowWrap && "whitespace-nowrap",
    allowWrap &&
      placement === "cell" &&
      "min-w-0 max-w-0 break-words whitespace-normal",
    allowWrap &&
      placement === "header" &&
      "whitespace-normal break-words [word-break:normal] min-w-[10rem]",
    meta?.align === "right" && "text-right",
    meta?.align === "center" && "text-center",
    (!meta?.align || meta?.align === "left") && "text-left"
  );
}

function isInteractiveClickTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'button, a, input, select, textarea, [role="checkbox"], [role="menuitem"]'
    )
  );
}

export interface DataTableProps<T extends BaseRowItem> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  currentPage?: number;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  totalPage?: number;
  limit?: number;
  count?: number;
  additionElement?: React.ReactNode;
  onRowClick?: (row: T) => void;
  rowSelection?: RowSelectionState;
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  getRowId?: (row: T) => string;
  footerLeftContent?: React.ReactNode;
  /**
   * `scroll` — horizontal scroll on narrow screens (default).
   * `cards` — stacked cards below `md`, full table from `md` up.
   */
  mobileLayout?: "scroll" | "cards";
  /** Shown when `data.length === 0` */
  emptyContent?: React.ReactNode;
  className?: string;
  /**
   * When set (e.g. `480px`, `55vh`), the table sits in a flex column with this
   * max-height scroll region; thead stays sticky; pagination footer stays below and does not scroll.
   */
  fixedBodyHeight?: string;
}

function DataTable<T extends BaseRowItem>({
  columns,
  data,
  currentPage,
  setCurrentPage,
  totalPage,
  count,
  rowSelection,
  setRowSelection,
  additionElement,
  limit = 10,
  onRowClick,
  getRowId,
  footerLeftContent,
  mobileLayout = "scroll",
  emptyContent,
  className,
  fixedBodyHeight,
}: DataTableProps<T>) {
  const selectionEnabled = Boolean(setRowSelection);

  const table = useReactTable({
    data,
    columns,
    ...(getRowId ? { getRowId: getRowId as (row: T) => string } : {}),
    ...(selectionEnabled && {
      onRowSelectionChange: setRowSelection,
      state: { rowSelection: rowSelection ?? {} },
      enableRowSelection: true,
    }),
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  const rows = table.getRowModel().rows;
  const showPaginationControls =
    typeof totalPage === "number" &&
    typeof currentPage === "number" &&
    typeof setCurrentPage === "function";
  const showCountText =
    typeof currentPage === "number" && typeof count === "number";

  const cellPadding = "px-3 py-3 text-left sm:px-6 sm:py-4 md:py-5";
  const headerPadding = "px-3 py-3 text-left sm:px-6 sm:py-4";

  const tableScrollWrapClass = clsx(
    "overflow-x-auto overscroll-x-contain bg-adminTable-canvas scrollbar-thin scrollbar-track-adminTable-canvas scrollbar-thumb-[#60B7F6] [-webkit-overflow-scrolling:touch]",
    mobileLayout === "scroll" && "min-w-0",
    fixedBodyHeight
      ? "overflow-y-auto rounded-t-xl border-x border-t border-adminTable-border"
      : "rounded-tl-lg border border-adminTable-border"
  );

  const tableSection = (
    <div className={tableScrollWrapClass} style={fixedBodyHeight ? { maxHeight: fixedBodyHeight } : undefined}>
      <table className="w-full min-w-max border-collapse text-left font-manrope">
        <thead className="sticky top-0 z-[2] bg-adminTable-header">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const allowWrap = meta?.wrap === true;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    className={clsx(
                      "border-b border-adminTable-border bg-adminTable-header text-[15px] font-medium normal-case text-adminTable-headerText sm:text-[16px]",
                      headerPadding,
                      metaAlignmentClasses(meta, "header"),
                      hideBelowClass(meta?.hideBelow)
                    )}
                    style={
                      allowWrap && meta?.maxWidth
                        ? { maxWidth: meta.maxWidth }
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody className="bg-adminTable-canvas">
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={(e) => {
                if (!onRowClick || isInteractiveClickTarget(e.target)) return;
                onRowClick(row.original);
              }}
              className={clsx(
                "border-b border-adminTable-border last:border-b-0",
                onRowClick &&
                  "cursor-pointer transition-colors hover:bg-adminTable-rowHover"
              )}
            >
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta;
                const allowWrap = meta?.wrap === true;
                return (
                  <td
                    key={cell.id}
                    className={clsx(
                      "text-[14px] font-medium text-white sm:text-[15px]",
                      cellPadding,
                      metaAlignmentClasses(meta, "cell"),
                      hideBelowClass(meta?.hideBelow)
                    )}
                    style={
                      allowWrap && meta?.maxWidth
                        ? { maxWidth: meta.maxWidth }
                        : undefined
                    }
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                );
              })}
            </tr>
          ))}

          {additionElement ? (
            <tr>
              <td colSpan={table.getAllColumns().length} className="p-0">
                {additionElement}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );

  const cardsSection =
    mobileLayout === "cards" ? (
      <div className="space-y-3 md:hidden">
        {rows.map((row) => (
          <div
            key={row.id}
            role={onRowClick ? "button" : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (!onRowClick) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(row.original);
              }
            }}
            onClick={(e) => {
              if (!onRowClick || isInteractiveClickTarget(e.target)) return;
              onRowClick(row.original);
            }}
            className={clsx(
              "rounded-lg border border-adminTable-border bg-adminTable-card p-4 font-manrope shadow-sm",
              onRowClick && "cursor-pointer hover:bg-adminTable-rowHover"
            )}
          >
            <dl className="space-y-2">
              {row.getVisibleCells().map((cell) => (
                <div
                  key={cell.id}
                  className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3"
                >
                  <dt className="shrink-0 text-xs font-medium uppercase tracking-wide text-adminTable-headerText">
                    {labelForColumn(cell.column)}
                  </dt>
                  <dd className="min-w-0 text-sm font-medium text-white sm:text-right">
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    ) : null;

  const isEmpty = data.length === 0;

  return (
    <div
      className={clsx(
        "w-full min-w-0 font-manrope",
        fixedBodyHeight && !isEmpty && "flex flex-col overflow-hidden",
        className
      )}
    >
      {isEmpty ? (
        <div className="rounded-lg border border-adminTable-border bg-adminTable-canvas px-4 py-12 text-center text-sm text-adminTable-muted">
          {emptyContent ?? "No data to display."}
        </div>
      ) : (
        <>
          {cardsSection &&
            (fixedBodyHeight && mobileLayout === "cards" ? (
              <div className="shrink-0">{cardsSection}</div>
            ) : (
              cardsSection
            ))}
          <div
            className={clsx(
              mobileLayout === "cards" ? "hidden md:block" : "",
              fixedBodyHeight &&
                mobileLayout === "cards" &&
                "md:flex md:min-h-0 md:flex-1 md:flex-col"
            )}
          >
            {tableSection}
          </div>
        </>
      )}

      {!isEmpty && (
        <div
          className={clsx(
            "flex w-full flex-col gap-3 border-t border-adminTable-border bg-adminTable-canvas px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-y-2 sm:px-6 sm:py-2",
            fixedBodyHeight && "shrink-0"
          )}
        >
          <div className="min-w-0 text-sm font-medium text-adminTable-muted">
            {footerLeftContent}
          </div>
          {showCountText && (
            <div className="order-last text-center text-xs font-medium text-adminTable-muted sm:order-none sm:flex-1 sm:text-sm">
              {getPaginationSummary(
                typeof currentPage === "number" ? currentPage : 1,
                limit,
                typeof count === "number" ? count : 0
              )}
            </div>
          )}
          {showPaginationControls && (
            <DataTablePagination
              totalPage={totalPage!}
              currentPage={currentPage!}
              setCurrentPage={setCurrentPage!}
              className="w-full justify-center sm:w-auto sm:justify-end"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default DataTable;
